import AppBackgroundColor from '@components/app-background-color/AppBackgroundColor';
import { ContentBlock } from '@layouts/ContentBlocks';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import makeStyles from '@ui/utils/makeStyles';
import { makeSeoData } from 'config/next-seo.config';
import Head from 'next/head';
import { NextSeo } from 'next-seo';
import React from 'react';

const useStyles = makeStyles()((theme) => ({
  imageWrapper: {
    color: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(2),
    height: 300,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    transition: 'all .2s',
    '&:hover': {
      transform: 'scale(1.025)'
    },
    [theme.breakpoints.up('md')]: {
      height: 400,
    },
    [theme.breakpoints.up('lg')]: {
      height: 555,
    }
  },
}));

const getImageStyle = imageUrl => `linear-gradient(rgba(8, 8, 8, .55) 100%, rgba(8, 8, 8, .55) 100%), url(${imageUrl})`;

const title = 'Press on our Home Staging Service and Décor Sales';
const description = 'Wondering what makes our home staging service and home décor store so unique? Read what the press has to say about us.';

export default function Press() {
  const { classes, theme } = useStyles();


  return (
    <>
      <AppBackgroundColor backgroundColor="#f5f1f0" />
      <Head>
        <title>
          Press | Guest House
        </title>
      </Head>
      <NextSeo {...makeSeoData({ pageTitle: title, metaTitle: title, metaDescription: description })} />

      <ContentBlock maxWidth={1365}>
        <Typography
          gutterBottom
          variant="h1"
        >
          Guest House in the News
        </Typography>
      </ContentBlock>

      <ContentBlock maxWidth={1365}>
        <Grid
          container
          spacing={4}
          alignContent="center"
          justifyContent="center"
          alignItems="center"
        >
          <Grid
            item
            xs={12}
            md={6}
          >
            <div
              className={classes.imageWrapper}
              data-bg-multi={getImageStyle('/press/5280.jpg')}
            >
              <Typography
                gutterBottom
                color="inherit"
                variant="h2"
              >
                5280
              </Typography>

              <Button
                href="https://www.5280.com/2018/09/the-coolest-new-way-to-shop-for-furnishings/"
                target="_blank"
                variant="contained"
                color="secondary"
                size="large"
              >
                Read More
              </Button>
            </div>
          </Grid>

          <Grid
            item
            xs={12}
            md={6}
          >
            <div
              className={classes.imageWrapper}
              data-bg-multi={getImageStyle('/press/modern-in-denver.jpg')}
            >
              <Typography
                gutterBottom
                color="inherit"
                variant="h2"
              >
                Modern in Denver
              </Typography>

              <Button
                href="https://view.publitas.com/modern-in-denver/modern-in-denver-fall-2018/page/86-87"
                target="_blank"
                variant="contained"
                color="secondary"
                size="large"
              >
                Read More
              </Button>
            </div>
          </Grid>
        </Grid>
      </ContentBlock>

      <ContentBlock maxWidth={1365}>

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          Robb Report
        </Typography>

        <Typography gutterBottom>
          No Visitors, Please: Why America’s Biggest Homes Are Designed to Discourage House Guests
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://robbreport.com/shelter/home-design/luxury-homes-no-visitors-1234623159/"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          San Diego Union-Tribune
        </Typography>

        <Typography gutterBottom>
          Staging can unlock a home’s star power for hopeful sellers
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://www.sandiegouniontribune.com/lifestyle/home-and-garden/story/2021-10-23/staging-basics"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          Modern in Denver
        </Typography>

        <Typography gutterBottom>
          All the World’s a Stage
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://view.publitas.com/modern-in-denver/modern-in-denver-fall-2018/page/86-87"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          5280
        </Typography>

        <Typography gutterBottom>
          The Coolest New Way to Shop for Furnishings
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://www.5280.com/2018/09/the-coolest-new-way-to-shop-for-furnishings/"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          Denver Business Journal/Colorado Inno
        </Typography>

        <Typography gutterBottom>
          Meet the 2021 Colorado Inno on Fire Honorees
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://www.bizjournals.com/denver/inno/stories/awards/2021/09/30/2021-colorado-inno-on-fire.html"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          Real Homes
        </Typography>

        <Typography gutterBottom>
          14 sunroom furniture ideas that are stylish and functional
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://www.realhomes.com/us/design/sunroom-furniture-ideas"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          Apartment Therapy
        </Typography>

        <Typography gutterBottom>
          The Controversial (and Kinda Weird) Home Staging Tactic That Annoys Real Estate Pros
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://www.apartmenttherapy.com/fake-food-home-staging-36968505"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          CO Chamber of Commerce
        </Typography>

        <Typography gutterBottom>
          How Three Home Furnishing Startups Leaned Into Post-COVID Living Trends to Drive Business
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://www.uschamber.com/co/good-company/launch-pad/brands-capitalize-on-post-covid-living-trends"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          Business Den
        </Typography>

        <Typography gutterBottom>
          Denver house staging startup raises $3M to expand in other states
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://businessden.com/2021/08/09/denver-house-staging-startup-raises-3m-to-expand-in-other-states/"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          San Diego Business Journal
        </Typography>

        <Typography gutterBottom>
          San Diego Native Bringing Home-Staging Business Home
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://www.sdbj.com/news/2021/aug/08/san-diego-native-bringing-home-staging-business-ho/"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          Voyage
        </Typography>

        <Typography gutterBottom>
          Meet Alex Ryden of Guest House
          {' '}
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="http://voyagedenver.com/interview/meet-alex-ryden-guest-house-denver/"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          Millionacres
        </Typography>

        <Typography gutterBottom>
          The Future of Staging with Alex Ryden
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://www.millionacres.com/resources/videos/millionacres-spotlight-the-future-of-staging-with-alex-ryden-guest-house/"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          Denver Business Journal/Colorado Inno
        </Typography>

        <Typography gutterBottom>
          New Money: These Colorado startups and technology companies raised nearly $370M in June
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://www.bizjournals.com/denver/inno/stories/roundups/2021/07/06/colorado-startups-june-2021-fundraise.html"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          Business of Home
        </Typography>

        <Typography gutterBottom>
          Tensions boil over at Great Jones, change at the top for Apparatus, and more
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://businessofhome.com/articles/tensions-boil-over-at-great-jones-change-at-the-top-for-apparatus-and-more"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          Oprah Daily
        </Typography>

        <Typography gutterBottom>
          50 Simple DIY Fall Decor Ideas for Your Home
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://www.oprahdaily.com/life/g36842344/diy-fall-decor-ideas/?slide=41"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          Built In Colorado
        </Typography>

        <Typography gutterBottom>
          Guest House Got $3M, Collective Retreats Is Growing, and More CO Tech News
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://www.builtincolorado.com/2021/06/14/colorado-tech-news-061421"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          Axios
        </Typography>

        <Typography gutterBottom>
          Axios Pro Rata
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://www.axios.com/newsletters/axios-pro-rata-dfe0211d-769b-4552-9d79-70d2bae7cb18.html"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          Crunchbase News
        </Typography>

        <Typography gutterBottom>
          Guest House Raises $3M Seed Round To Stage Shoppable Homes
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://news.crunchbase.com/news/guest-house-raises-3m-seed-round-to-stage-shoppable-homes/"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          Business Insider
        </Typography>

        <Typography gutterBottom>
          This pitch deck helped a startup that decorates on-the-market homes with furniture for sale raise $3 million
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://www.businessinsider.com/guest-house-pitch-deck-home-staging-furniture-retail-startup-funding-2021-6"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          Denver Business Journal/Colorado Inno
        </Typography>

        <Typography gutterBottom>
          Denver startup building &apos;Postmates for staging&apos; homes raises $3M seed round
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://www.bizjournals.com/denver/inno/stories/fundings/2021/06/08/guest-house-range-ventures-home-staging.html"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          Inman News
        </Typography>

        <Typography gutterBottom>
          Staging and marketing company Guest House raises $3M
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://www.inman.com/2021/06/08/staging-and-marketing-company-guest-house-raises-3m/"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          Built In Colorado
        </Typography>

        <Typography gutterBottom>
          Denver-Based Home Staging Startup Guest House Raises $3M Seed Round
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://www.builtincolorado.com/2021/06/08/guest-house-raises-3m-seed-round-denver-hiring"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          5280
        </Typography>

        <Typography gutterBottom>
          15 Brilliant Minds Shaping The Way Denverites Live
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://www.5280.com/2020/06/15-brilliant-minds-shaping-the-way-denverites-live/"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          Colorado Homes &amp; Lifestyles
        </Typography>

        <Typography gutterBottom>
          Homes You Can Shop
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://www.coloradohomesmag.com/homes-you-can-shop/"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          5280
        </Typography>

        <Typography gutterBottom>
          This Home Decor Business Is Giving 100 Percent of Its Profits to Local Makers
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://www.5280.com/2020/04/this-home-decor-business-is-giving-100-percent-of-its-profits-to-local-makers/"
          >
            Read More
          </Link>
        </Typography>

        <Divider style={{ margin: theme.spacing(3, 0) }} />

        <Typography
          gutterBottom
          variant="h5Alt"
          component="h3"
        >
          Business Den
        </Typography>

        <Typography gutterBottom>
          The home is for sale. When this startup is involved, so is the furniture inside.
        </Typography>

        <Typography gutterBottom>
          <Link
            target="_blank"
            href="https://businessden.com/2019/02/26/the-home-is-for-sale-when-this-startup-is-involved-so-is-the-furniture-inside/"
          >
            Read More
          </Link>
        </Typography>
      </ContentBlock>
    </>
  );
}
